import { Nullable } from '@common/type/CommonTypes';
import { CoreApiResponse, CoreApiStatusResponse } from '@common/type/CoreApiResponseTypes';
import { ProductBrand } from '@common/type/GoogleTagType';
import { PaginateResponse } from '@common/type/PaginateType';
import { ProductAffiliation } from '@common/type/ProductType';
import CurriculumArticleContentStrategy from '@core/domain/course/strategy/CurriculumArticleContentStrategy';
import CurriculumAssignmentContentStrategy from '@core/domain/course/strategy/CurriculumAssignmentContentStrategy';
import CurriculumExaminationContentStrategy from '@core/domain/course/strategy/CurriculumExaminationContentStrategy';
import CurriculumQuizContentStrategy from '@core/domain/course/strategy/CurriculumQuizContentStrategy';
import CurriculumSurveyContentStrategy from '@core/domain/course/strategy/CurriculumSurveyContentStrategy';
import CurriculumVideoContentStrategy from '@core/domain/course/strategy/CurriculumVideoContentStrategy';
import { LearningTypeId } from 'src/types/product';

import { ProductPrice } from './productType';

import { COURSE_ITEMS_CONTENT_TYPE, QUIZ_TYPE } from '@helpers/constants';
import { ICourse } from '@interfaces/course';
import { ISubtitle } from '@interfaces/learning';

export type FetchCourseDetailSeoRequestAction = {
  courseIdOrIdName: string;
};

export type CourseInstructorEntity = {
  fullName: string;
  biology: string;
  slug: string;
};

export type CourseUniversityEntity = {
  name: string;
  hasLandingPage: boolean;
  slug: string;
};

export type CourseDegreeEntity = {
  name: string;
  friendlyUrl: string;
};

export type CourseEntity = {
  id: number;
  idName: string;
  name: string;
  description: string;
  seoDescription: string;
  seoEnable: boolean;
  fbContent: string;
  shareThumbnailUrl: string;
  commercialMessage: string;
  instructors: CourseInstructorEntity[];
  degrees: CourseDegreeEntity[];
  university: CourseUniversityEntity;
  productLearningTypeId: LearningTypeId;
};

// Course detail seo
export type FetchCourseDetailSeoState = CourseEntity;

export type FetchCourseDetailSeoSuccessAction = FetchCourseDetailSeoState;

export type FetchCourseDetailSeoErrorAction = unknown;

export type HttpFetchCourseDetailSeoStatusResponse = CoreApiStatusResponse;

export type HttpFetchCourseDetailSeoApiResponse = CoreApiResponse<
  FetchCourseDetailSeoSuccessAction,
  HttpFetchCourseDetailSeoStatusResponse
>;

//  Course detail
export type FetchCourseDetailRequestAction = {
  courseIdOrIdName: string;
};

export type CriteriaAssessmenScoreEntity = {
  quizName: string;
  score: number;
};

export type CourseDetailEntity = {
  highlightImage2xUrl: string;
  highlightImage1xUrl: string;
  universityLogoUrl: string;
  hasCertificate: boolean;
  hasCertificateBundle: boolean;
  criteriaCertificate?: Partial<{
    learnPercent: number;
    assessmentsScore: CriteriaAssessmenScoreEntity[];
  }>;
  gtmContentId: string;
  gtmProductBrand: ProductBrand;
  mainCategory: string;
  subCategory: string;
  productAffiliation: ProductAffiliation;
  price: {
    regular?: ProductPrice;
    audit?: ProductPrice;
    credit?: ProductPrice;
    upgrade?: ProductPrice;
  };
  videoCount: number;
  totalVideoDuration: number;
  countdownDay: number;
  attachmentFileCount: number;
  quizCount: number;
  assignmentCount: number;
  examinationCount: number;
  articleCount: number;
  review?: {
    reviewAverageRating: number;
    totalReview: number;
  };
  isUseMailTemplate: boolean;
  mailContent: string;
  mailSubject: string;
  tweeterContent: string;
  useVideoPreviewCourseBanner?: boolean;
  videoPreviewUrl?: string;
  videoPreviewSubtitles?: ISubtitle[];
  bannerPrimaryColor: string;
  bannerSecondaryColor: string;
  degreeCourseGroups: Nullable<
    {
      productNames: string[];
      degreeName: string;
      majorName: string;
      facultyName: string;
      urlPdfCourseStructureAndCondition: Nullable<string>;
    }[]
  >;
};

export type FetchCourseDetailState = CourseDetailEntity;

export type FetchCourseDetailSuccessAction = FetchCourseDetailState;

export type FetchCourseDetailErrorAction = unknown;

export type HttpFetchCourseDetailStatusResponse = CoreApiStatusResponse;

export type HttpFetchCourseDetailApiResponse = CoreApiResponse<
  FetchCourseDetailSuccessAction,
  HttpFetchCourseDetailStatusResponse
>;

//  Course enrollment
export type FetchCourseEnrollmentRequestAction = {
  courseId: number;
};

export enum RegularCourseEnrollmentStatus {
  ENROLLMENT_EXPIRED = 'enrollment_expired',
  ENROLLED = 'enrolled',
  UNPUBLISHED = 'unpublished',
  PUBLISHED = 'published',
  AVAILABLE_REGISTER = 'available_register',
}

export enum AcademicCourseEnrollmentStatus {
  ACADEMIC_AUDIT_ENROLLED = 'academic_audit_enrolled',
  ACADEMIC_AUDIT_ENROLLMENT_EXPIRED = 'academic_audit_enrollment_expired',
  ACADEMIC_CREDIT_PENDING = 'academic_credit_pending',
  ACADEMIC_CREDIT_PASSED = 'academic_credit_passed',
  ACADEMIC_CREDIT_PASSED_REGRADE = 'academic_credit_passed_regrade',
  ACADEMIC_CREDIT_REGRADE = 'academic_credit_regrade',
  ACADEMIC_CREDIT_ENROLLED = 'academic_credit_enrolled',
  ACADEMIC_CREDIT_ENROLLMENT_EXPIRED = 'academic_credit_enrollment_expired',
  UNPUBLISHED = 'unpublished',
  PUBLISHED = 'published',
  AVAILABLE_REGISTER = 'available_register',
}

// TODO: implement `or` condition for academic course
export type CourseEnrollmentStatus = RegularCourseEnrollmentStatus | AcademicCourseEnrollmentStatus;

export type CourseEnrollmentEntity = {
  enrollmentId?: Nullable<number>;
  enrollmentStatus: CourseEnrollmentStatus;
  hasEnrolled: boolean;
  canDiscuss: boolean;
  examExpiredAt?: Date;
};

export type FetchCourseEnrollmentState = CourseEnrollmentEntity;

export type FetchCourseEnrollmentSuccessAction = FetchCourseEnrollmentState;

export type FetchCourseEnrollmentErrorAction = unknown;

export type HttpFetchCourseEnrollmentStatusResponse = CoreApiStatusResponse;

export type HttpFetchCourseEnrollmentApiResponse = CoreApiResponse<
  FetchCourseEnrollmentSuccessAction,
  HttpFetchCourseEnrollmentStatusResponse
>;

// Course Detail Glances
export type FetchCourseDetailGlancesRequestAction = {
  courseIdOrIdName: string;
};

export type CourseDetailGlancesEntity = {
  glances: {
    name: string;
  }[];
};

export type FetchCourseDetailGlancesState = CourseDetailGlancesEntity;

export type FetchCourseDetailGlancesSuccessAction = FetchCourseDetailGlancesState;

export type FetchCourseDetailGlancesErrorAction = unknown;

export type HttpFetchCourseDetailGlancesStatusResponse = CoreApiStatusResponse;

export type HttpFetchCourseDetailGlancesApiResponse = CoreApiResponse<
  FetchCourseDetailGlancesSuccessAction,
  HttpFetchCourseDetailGlancesStatusResponse
>;

// Course Detail Curriculum
export type FetchCourseDetailCurriculumRequestAction = {
  courseIdOrIdName: string;
};

export type CourseItemsContentType = Exclude<COURSE_ITEMS_CONTENT_TYPE, COURSE_ITEMS_CONTENT_TYPE.INFOGRAPHIC>;

export type CourseCurriculumContentEntity = {
  id: number;
  name: string;
  duration?: number;
  attachmentCount: number;
  type: COURSE_ITEMS_CONTENT_TYPE;
};

export type CourseCurriculumArticleContentEntity = CourseCurriculumContentEntity & {
  type: COURSE_ITEMS_CONTENT_TYPE.ARTICLE;
};

export type CourseCurriculumQuizContentEntity = CourseCurriculumContentEntity & {
  type: COURSE_ITEMS_CONTENT_TYPE.QUIZ;
  quizType: QUIZ_TYPE.REGULAR;
};

export type CourseCurriculumAssignmentContentEntity = CourseCurriculumContentEntity & {
  type: COURSE_ITEMS_CONTENT_TYPE.QUIZ;
  quizType: QUIZ_TYPE.ASSIGNMENT;
};

export type CourseCurriculumExaminationContentEntity = CourseCurriculumContentEntity & {
  type: COURSE_ITEMS_CONTENT_TYPE.QUIZ;
  quizType: QUIZ_TYPE.EXAMINATION;
};

export type CourseCurriculumSurveyContentEntity = CourseCurriculumContentEntity & {
  type: COURSE_ITEMS_CONTENT_TYPE.SURVEY;
};

export type CourseCurriculumVideoContentEntity = CourseCurriculumContentEntity & {
  type: COURSE_ITEMS_CONTENT_TYPE.VIDEO;
  isPreview: boolean;
};

export type CourseCurriculumContentTypeEntity =
  | CourseCurriculumArticleContentEntity
  | CourseCurriculumQuizContentEntity
  | CourseCurriculumAssignmentContentEntity
  | CourseCurriculumExaminationContentEntity
  | CourseCurriculumSurveyContentEntity
  | CourseCurriculumVideoContentEntity;

export type CourseCurriculumContentStrategy =
  | CurriculumArticleContentStrategy
  | CurriculumQuizContentStrategy
  | CurriculumAssignmentContentStrategy
  | CurriculumExaminationContentStrategy
  | CurriculumSurveyContentStrategy
  | CurriculumVideoContentStrategy;

export type CourseDetailCurriculumEntity = {
  partId: number;
  partName: string;
  contents: CourseCurriculumContentTypeEntity[];
}[];

export type FetchCourseDetailCurriculumState = CourseDetailCurriculumEntity;

export type FetchCourseDetailCurriculumSuccessAction = FetchCourseDetailCurriculumState;

export type FetchCourseDetailCurriculumErrorAction = unknown;

export type HttpFetchCourseDetailCurriculumStatusResponse = CoreApiStatusResponse;

export type HttpFetchCourseDetailCurriculumApiResponse = CoreApiResponse<
  FetchCourseDetailCurriculumSuccessAction,
  HttpFetchCourseDetailCurriculumStatusResponse
>;

// Course Detail Review
export type FetchCourseReviewListRequestAction = {
  courseIdOrIdName: string;
  limit: number;
  offset: number;
};

export type CourseReviewEntity = {
  id: number;
  userId: number;
  userAvatar: string;
  reviewerName: string;
  rating: number;
  comment?: string;
  updateAt: string;
};

export type CourseReviewListEntity = {
  reviews: CourseReviewEntity[];
};

export type CourseReviewListPagination<T> = T & {
  count: number;
  hasMore: boolean;
  page: number;
};

export type FetchCourseReviewListState = CourseReviewListPagination<CourseReviewListEntity>;

export type FetchCourseReviewListSuccessAction = FetchCourseReviewListState;

export type FetchCourseReviewListErrorAction = unknown;

export type HttpFetchCourseReviewListStatusResponse = CoreApiStatusResponse;

export type HttpFetchCourseReviewListApiResponse = CoreApiResponse<
  FetchCourseReviewListSuccessAction,
  HttpFetchCourseReviewListStatusResponse
>;

export type FetchCourseReviewMeRequestAction = {
  courseIdOrIdName: string;
};

export type FetchCourseReviewMeState = CourseReviewEntity;

export type FetchCourseReviewMeSuccessAction = FetchCourseReviewMeState;

export type FetchCourseReviewMeErrorAction = unknown;

export type HttpFetchCourseReviewMeStatusResponse = CoreApiStatusResponse;

export type HttpFetchCourseReviewMeApiResponse = CoreApiResponse<
  FetchCourseReviewMeSuccessAction,
  HttpFetchCourseReviewMeStatusResponse
>;

export type FetchCourseReviewSummaryRequestAction = {
  courseIdOrIdName: string;
};

export type CourseReviewSummaryEntity = {
  averageRating: number;
  count: number;
  ratings: {
    rating: number;
    totalReviewer: number;
  }[];
};

export type FetchCourseReviewSummaryState = CourseReviewSummaryEntity;

export type FetchCourseReviewSummarySuccessAction = FetchCourseReviewSummaryState;

export type FetchCourseReviewSummaryErrorAction = unknown;

export type HttpFetchCourseReviewSummaryStatusResponse = CoreApiStatusResponse;

export type HttpFetchCourseReviewSummaryApiResponse = CoreApiResponse<
  FetchCourseReviewSummarySuccessAction,
  HttpFetchCourseReviewSummaryStatusResponse
>;

export type CreateOrUpdateCourseReviewRequestAction = {
  courseIdOrIdName: string;
  rating: number;
  comment?: string;
};

export type CreateOrUpdateCourseReviewEntity = {
  averageRating: number;
  count: number;
  ratings: {
    rating: number;
    totalReviewer: number;
  }[];
};

export type CreateOrUpdateCourseReviewState = CreateOrUpdateCourseReviewEntity;

export type CreateOrUpdateCourseReviewSuccessAction = CreateOrUpdateCourseReviewState;

export type CreateOrUpdateCourseReviewErrorAction = unknown;

export type HttpCreateOrUpdateCourseReviewStatusResponse = CoreApiStatusResponse;

export type HttpCreateOrUpdateCourseReviewApiResponse = CoreApiResponse<
  CreateOrUpdateCourseReviewSuccessAction,
  HttpCreateOrUpdateCourseReviewStatusResponse
>;

// Course Detail Discussion
export type FetchCourseDiscussionListRequestAction = {
  courseIdOrIdName: string;
  page: number;
  pageSize: number;
  title: string;
};

export type CourseDiscussionReplyEntity = {
  id: number;
  content: string;
  createdAt: Date;
  discussionId: number;
  user: {
    id: number;
    fullname: string;
    imageUrl?: string;
    isAuthor?: boolean;
  };
};

export type CourseDiscussionEntity = {
  id: number;
  title: string;
  content?: string;
  createdAt: Date;
  discussionReplies: CourseDiscussionReplyEntity[];
  user: {
    id: number;
    fullname: string;
    imageUrl?: string;
    isAuthor?: boolean;
  };
};

export type FetchCourseDiscussionListState = PaginateResponse<CourseDiscussionEntity>;

export type FetchCourseDiscussionListSuccessAction = FetchCourseDiscussionListState;

export type FetchCourseDiscussionListErrorAction = unknown;

export type HttpFetchCourseDiscussionListStatusResponse = CoreApiStatusResponse;

export type HttpFetchCourseDiscussionListApiResponse = CoreApiResponse<
  FetchCourseDiscussionListSuccessAction,
  HttpFetchCourseDiscussionListStatusResponse
>;

export type CreateCourseDiscussionRequestAction = {
  courseIdOrIdName: string;
  title: string;
};

export type CreateCourseDiscussionEntity = {
  id: number;
  title: string;
  content?: string;
  createdAt: Date;
  user: {
    id: number;
    fullname: string;
    imageUrl?: string;
    isAuthor?: boolean;
  };
};

export type CreateCourseDiscussionState = CreateCourseDiscussionEntity;

export type CreateCourseDiscussionSuccessAction = CreateOrUpdateCourseReviewState;

export type CreateCourseDiscussionErrorAction = unknown;

export type HttpCreateCourseDiscussionStatusResponse = CoreApiStatusResponse;

export type HttpCreateCourseDiscussionApiResponse = CoreApiResponse<
  CreateCourseDiscussionSuccessAction,
  HttpCreateCourseDiscussionStatusResponse
>;

export type AddCourseDiscussionReplyRequestAction = {
  courseIdOrIdName: string;
  discussionId: number;
  content: string;
};

export type AddCourseDiscussionReplyState = CourseDiscussionReplyEntity;

export type AddCourseDiscussionReplySuccessAction = AddCourseDiscussionReplyState;

export type AddCourseDiscussionReplyErrorAction = unknown;

export type HttpAddCourseDiscussionReplyStatusResponse = CoreApiStatusResponse;

export type HttpAddCourseDiscussionReplyApiResponse = CoreApiResponse<
  AddCourseDiscussionReplySuccessAction,
  HttpAddCourseDiscussionReplyStatusResponse
>;

export type FetchCurriculumVideoPreviewRequestAction = {
  courseIdOrIdName: string;
  chapterId: number;
};

export type CurriculumVideoPreviewEntity = {
  videoUrl: string;
  subtitles: ISubtitle[];
};

export type FetchCurriculumVideoPreviewState = CurriculumVideoPreviewEntity;

export type FetchCurriculumVideoPreviewSuccessAction = FetchCurriculumVideoPreviewState;

export type FetchCurriculumVideoPreviewErrorAction = unknown;

export type HttpFetchCurriculumVideoPreviewStatusResponse = CoreApiStatusResponse;

export type HttpFetchCurriculumVideoPreviewApiResponse = CoreApiResponse<
  FetchCurriculumVideoPreviewSuccessAction,
  HttpFetchCurriculumVideoPreviewStatusResponse
>;

export type FetchCourseCertificateBundlesRequestAction = {
  courseId: number;
};

export type CourseCertificateBundlesEntity = {
  id: number;
  name: string;
  cardImage: string;
};

export type FetchCourseCertificateBundlesState = CourseCertificateBundlesEntity[];

export type FetchCourseCertificateBundlesSuccessAction = FetchCourseCertificateBundlesState;

export type FetchCourseCertificateBundlesErrorAction = unknown;

export type HttpFetchCourseCertificateBundlesStatusResponse = CoreApiStatusResponse;

export type HttpFetchCourseCertificateBundlesApiResponse = CoreApiResponse<
  FetchCourseCertificateBundlesSuccessAction,
  HttpFetchCourseCertificateBundlesStatusResponse
>;
export type FetchCourseSuggestionsRequestAction = {
  courseIdOrIdName: string;
};

export type CourseSuggestionsEntity = {
  courses: ICourse[];
};

export type FetchCourseSuggestionsState = CourseSuggestionsEntity;

export type FetchCourseSuggestionsSuccessAction = FetchCourseSuggestionsState;

export type FetchCourseSuggestionsErrorAction = unknown;

export type HttpFetchCourseSuggestionsStatusResponse = CoreApiStatusResponse;

export type HttpFetchCourseSuggestionsApiResponse = CoreApiResponse<
  FetchCourseSuggestionsSuccessAction,
  HttpFetchCourseSuggestionsStatusResponse
>;
